
import type { Result } from '@empathyco/x-types'
import type { PropType } from 'vue'
import type { ExplainMessages } from '../../models/i18n/explain.messages'

import type { StorageService } from '../../services'

import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { ClearFilters } from '@empathyco/x-components/facets'
import { defineComponent, getCurrentInstance } from 'vue'
import { mapGetters } from 'vuex'
import { useSemantics } from '../../composables/use-semantics/use-semantics'
import { useX } from '../../composables/x/use-x'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { UserUtils } from '../../utils/user.utils'
import CollapsableLayout from '../collapsable-layout.component.vue'
import SemanticPreviewList from '../semantics/semantic-preview-list.component.vue'
import { EpbButton } from '../shared/epb-button'
import XFacets from '../x/facets/x-facets.component.vue'
import XSelectedFiltersList from '../x/facets/x-selected-filters-list.component.vue'
import XResultsGrid from '../x/x-results-grid.component.vue'
import ExplainResultCardFooter from './explain-result-card-footer.component.vue'
import ExplainResultsLegend from './explain-results-legend.component.vue'

const COLLAPSE_FACETS_STORAGE_KEY = 'explain-result-grid-facets-collapsed'

export default defineComponent({
  name: 'ExplainResultGrid',
  components: {
    SemanticPreviewList,
    EpbButton,
    XSelectedFiltersList,
    ClearFilters,
    ExplainResultCardFooter,
    XResultsGrid,
    XFacets,
    CollapsableLayout,
    ExplainResultsLegend,
  },
  props: {
    resultsHoverDisabled: Boolean,
    selectedResultIds: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    messages: {
      type: Object as PropType<ExplainMessages>,
      required: true,
    },
  },
  setup() {
    const { enabled: semanticsEnabled } = useSemantics()
    const {
      proxy: { $router, $epb },
    } = getCurrentInstance()

    const query = useX().query

    const hasUserProductAnalysisPermission =
      $epb?.oidcLogin ||
      UserUtils.hasPermission(
        ROUTER_CONSTS.routes[FeatureIdEnum.EXPLAIN].productAnalysis,
        PermissionLevelEnum.READ,
      )

    function onViewProductAnalysisClick(result: Result): void {
      $router.push({
        name: ROUTER_CONSTS.routes[FeatureIdEnum.EXPLAIN].productAnalysis,
        params: {
          productId: result.id.toString(),
        },
      })
    }

    return {
      query,
      semanticsEnabled,
      onViewProductAnalysisClick,
      hasUserProductAnalysisPermission,
    }
  },
  data() {
    return {
      baseClass: 'epb-explain-result-grid',
      areFacetsCollapsed:
        CONTAINER.get<StorageService>(TYPES.StorageService).getItem(COLLAPSE_FACETS_STORAGE_KEY) !==
        'false',
      data: [],
      loading: true,
    }
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    }),
    totalResults(): number {
      return this.$x.totalResults
    },
    hasResults(): boolean {
      return this.$x.results.length
    },
  },
  watch: {
    areFacetsCollapsed(): void {
      CONTAINER.get<StorageService>(TYPES.StorageService).setItem(
        COLLAPSE_FACETS_STORAGE_KEY,
        this.areFacetsCollapsed,
      )
    },
    '$x.status.search': {
      handler(status: string): void {
        if (status === 'loading') {
          this.loading = true
        } else {
          this.loading = false
        }
      },
    },
  },
})
